import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Table, Spinner, Form, ButtonGroup, Button, InputGroup, Collapse } from "react-bootstrap";
import { FaExclamationTriangle, FaSearch, FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { getFullName } from "../../../utils/helpers";
import {
  fetchTimeDistributionData,
  fetchWorkPackagesForProject,
  generateTimeDistribution,
} from "../../../redux/slices/timeDistributionSlice";

import ProjectRow from "./row/ProjectRow";
import ResourceRow from "./row/ResourceRow";
import WorkPackageRow from "./row/WorkPackageRow";


const PROJECT_TYPES = {
  FINANCED: "Financed",
  CLIENT: "Client",
  INTERNAL: "Internal",
  ALL: "",
};

const VIEW_MODES = {
  RESOURCES: "resources",
  WORK_PACKAGES: "workpackages",
};

const PROJECT_TYPE_OPTIONS = [
  { value: PROJECT_TYPES.FINANCED, labelKey: "components.planningTab.project_types.financed" },
  { value: PROJECT_TYPES.CLIENT, labelKey: "components.planningTab.project_types.client" },
  { value: PROJECT_TYPES.INTERNAL, labelKey: "components.planningTab.project_types.internal" },
  { value: PROJECT_TYPES.ALL, labelKey: "components.planningTab.project_types.all" },
];

const normalize = (str) => (str || "").toLowerCase();

const getMonthKey = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
);

const changeMonth = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);

const PlanningTab = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const { 
    allocations, 
    isLoading, 
    error, 
    workPackagesByProject, 
    loadingPackagesFor,
    distributionExists,
    distributionStatus,
  } = useSelector((state) => state.timeDistribution);
  
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedPackages, setExpandedPackages] = useState(new Set());
  const [projectType, setProjectType] = useState(PROJECT_TYPES.FINANCED);
  const [viewModeByProject, setViewModeByProject] = useState({});
  const [searchByProject, setSearchByProject] = useState({});
  const [showConflicts, setShowConflicts] = useState(false);

  const selectedMonthKey = getMonthKey(selectedMonth);
  const selectedMonthLabel = useMemo(() => (
    new Intl.DateTimeFormat(i18n.language, {
      month: "long",
      year: "numeric",
    }).format(selectedMonth)
  ), [i18n.language, selectedMonth]);
  const requiresRegeneration = distributionStatus === "needs_regeneration";

  useEffect(() => {
    setExpandedProjects(new Set());
    setExpandedPackages(new Set());
    setViewModeByProject({});
    setSearchByProject({});
    setShowConflicts(false);
    dispatch(fetchTimeDistributionData({ month: selectedMonthKey }));
  }, [dispatch, selectedMonthKey]);

  const filteredAllocations = useMemo(() => {
    if (!allocations) return [];
    if (!projectType) return allocations;
    return allocations.filter((project) => project.project_type === projectType);
  }, [allocations, projectType]);

  const allConflicts = useMemo(() => {
    const seen = new Map();
    filteredAllocations?.forEach(project => {
      project.resources?.forEach(resource => {
        if (resource.has_conflict && !seen.has(resource.id)) {
          seen.set(resource.id, resource);
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => {
      const nameA = getFullName(a).toLowerCase();
      const nameB = getFullName(b).toLowerCase();
      return nameA.localeCompare(nameB, 'pt');
    });
  }, [filteredAllocations]);

  const toggleProject = (project) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(project.id)) {
        next.delete(project.id);
      } else {
        next.add(project.id);
        if (!viewModeByProject[project.id]) {
          setViewModeByProject((v) => ({ ...v, [project.id]: VIEW_MODES.RESOURCES }));
        }
        dispatch(fetchWorkPackagesForProject({ projectId: project.id, month: selectedMonthKey }));
      }
      return next;
    });
  };

  const togglePackage = (wpId) => {
    setExpandedPackages((prev) => {
      const next = new Set(prev);
      if (next.has(wpId)) {
        next.delete(wpId);
      } else {
        next.add(wpId);
      }
      return next;
    });
  };

  const setViewMode = (projectId, mode) => {
    setViewModeByProject((prev) => ({ ...prev, [projectId]: mode }));
  };

  const setSearch = (projectId, value) => {
    setSearchByProject((prev) => ({ ...prev, [projectId]: value }));
  };

  const handleGenerateDistribution = async () => {
    try {
      await dispatch(generateTimeDistribution({ month: selectedMonthKey })).unwrap();
      await dispatch(fetchTimeDistributionData({ month: selectedMonthKey })).unwrap();
    } catch (generationError) {
      console.error(t("components.planningTab.errors.generate"), generationError);
    }
  };

  return (
    <div className="fade-in">

      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center gap-1">
          <Button
            variant="light"
            size="sm"
            onClick={() => setSelectedMonth((month) => changeMonth(month, -1))}
            className="border-0 shadow-sm txt-main bg-surface d-flex align-items-center justify-content-center p-0"
            style={{ borderRadius: "6px", width: "32px", height: "32px" }}
            aria-label={t("components.planningTab.month_navigation.previous")}
          >
            <FaChevronLeft size={11} />
          </Button>

          <span className="fw-bold txt-main text-capitalize text-center px-2" style={{ minWidth: "160px" }}>
            {selectedMonthLabel}
          </span>

          <Button
            variant="light"
            size="sm"
            onClick={() => setSelectedMonth((month) => changeMonth(month, 1))}
            className="border-0 shadow-sm txt-main bg-surface d-flex align-items-center justify-content-center p-0"
            style={{ borderRadius: "6px", width: "32px", height: "32px" }}
            aria-label={t("components.planningTab.month_navigation.next")}
          >
            <FaChevronRight size={11} />
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setSelectedMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
            className="ms-1 fw-bold"
            style={{ borderRadius: "6px", fontSize: "0.75rem" }}
          >
            {t("components.planningTab.month_navigation.today")}
          </Button>
        </div>

        {distributionExists && (
          <div className="d-flex align-items-center gap-2">
            {requiresRegeneration && (
              <Button
                variant="warning"
                size="sm"
                onClick={handleGenerateDistribution}
                disabled={isLoading}
              >
                {t("components.planningTab.regeneration.generate")}
              </Button>
            )}
            <Form.Select
              value={projectType}
              onChange={(e) => setProjectType(e.target.value)}
              style={{ width: "220px", fontSize: "0.85rem" }}
              size="sm"
            >
              {PROJECT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.labelKey} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
          </div>
        )}
      </div>

      {isLoading && distributionExists === null ? (
        <div className="d-flex justify-content-center align-items-center p-5 min-vh-50">
          <Spinner animation="border" style={{ color: "var(--brand-primary)" }} />
        </div>
      ) : error && distributionExists !== false && (!allocations || allocations.length === 0) ? (
        <div className="theme-alert-danger m-4 border-0 shadow-sm rounded-3">
          <strong>{t("components.planningTab.errors.loading")}</strong>{" "}
          {typeof error === "string" ? error : error?.detail || t("components.planningTab.errors.unknown")}
        </div>
      ) : distributionExists === false ? (
        <Card className="atlar-card d-flex align-items-center justify-content-center text-center p-4" style={{ minHeight: "420px" }}>
          <div style={{ maxWidth: "460px" }}>
            <h5 className="fw-bold txt-main mb-2">
              {t("components.planningTab.empty_month.title", { month: selectedMonthLabel })}
            </h5>
            <p className="txt-subtle mb-4">
              {t("components.planningTab.empty_month.description")}
            </p>
            <Button
              variant="primary"
              onClick={handleGenerateDistribution}
              disabled={isLoading}
              className="px-4 py-2 fw-semibold"
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : t("components.planningTab.empty_month.generate")}
            </Button>
          </div>
        </Card>
      ) : (
        <>

      {allConflicts.length > 0 && (
        <div className="mb-4 shadow-sm rounded-3">
          <Button
            variant="light"
            className="theme-alert-danger d-flex align-items-center justify-content-between w-100 p-3 border-0 m-0"
            style={{ 
              borderRadius: showConflicts ? "var(--border-radius) var(--border-radius) 0 0" : "var(--border-radius)" 
            }}
            onClick={() => setShowConflicts(!showConflicts)}
          >
            <div className="d-flex align-items-center gap-2 fw-bold">
              <FaExclamationTriangle size={18} />
              {t('components.planningTab.conflicts.detected', { count: allConflicts.length })}
            </div>
            <FaChevronDown 
              style={{ 
                transform: showConflicts ? "rotate(180deg)" : "rotate(0deg)", 
                transition: "transform 0.3s ease" 
              }} 
            />
          </Button>
          
          <Collapse in={showConflicts}>
            <div 
              className="bg-surface border border-top-0 px-3 py-2" 
              style={{ 
                borderRadius: "0 0 var(--border-radius) var(--border-radius)", 
                borderColor: "rgba(239, 68, 68, 0.2)" 
              }}
            >
              <div style={{ maxHeight: "250px", overflowY: "auto" }} className="custom-scrollbar">
                {allConflicts.map((conflict, index) => (
                  <div 
                    key={index} 
                    className="py-2 txt-danger d-flex align-items-center gap-2" 
                    style={{ 
                      fontSize: "0.85rem", 
                      borderBottom: index !== allConflicts.length - 1 ? "1px solid rgba(239, 68, 68, 0.1)" : "none" 
                    }}
                  >
                    <div style={{ width: "6px", height: "6px", backgroundColor: "var(--danger-red)", borderRadius: "50%" }}></div>
                    <span>
                      <span className="fw-semibold">{getFullName(conflict)}</span> {t('components.planningTab.conflicts.exceeds_limit')} ({conflict.total_eti?.toFixed(2)} / 1.00)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Collapse>
        </div>
      )}

      <Card className="atlar-card overflow-hidden">
        <Table hover responsive align="middle" className="mb-0">
          <thead className="bg-surface txt-subtle text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
            <tr>
              <th className="py-3 px-4">{t('components.planningTab.table.structure')}</th>
              <th className="py-3 px-3 text-center">{t('components.planningTab.table.capacity')}</th>
              <th className="py-3 px-4 text-end">{t('components.planningTab.table.status')}</th>
              <th className="py-3 px-3 text-center" style={{ width: "40px" }}>{t('components.planningTab.table.details')}</th>
            </tr>
          </thead>
          <tbody>

            {filteredAllocations && filteredAllocations.length > 0 ? (
              filteredAllocations.map((project) => {
                const isProjectExpanded = expandedProjects.has(project.id);
                const workPackageCacheKey = `${selectedMonthKey}:${project.id}`;
                const workPackages = workPackagesByProject[workPackageCacheKey] || [];
                const isLoadingPackages = loadingPackagesFor.includes(workPackageCacheKey);
                
                const viewMode = viewModeByProject[project.id] || VIEW_MODES.RESOURCES;
                
                const search = normalize(searchByProject[project.id]);

                const filteredResources = [...(project.resources || [])]
                  .filter((r) => normalize(getFullName(r)).includes(search))
                  .sort((a, b) => getFullName(a).toLowerCase().localeCompare(getFullName(b).toLowerCase(), 'pt'));

                const filteredWorkPackages = workPackages
                  .map((wp) => {
                    const wpMatches = normalize(wp.name).includes(search);
                    const matchingMembers = wp.members.filter((m) =>
                      normalize(getFullName(m)).includes(search)
                    );
                    if (!search) return wp;
                    if (wpMatches) return wp;
                    if (matchingMembers.length > 0) return { ...wp, members: matchingMembers };
                    return null;
                  })
                  .filter(Boolean);

                return (
                  <React.Fragment key={`project-${project.id}`}>
                    <ProjectRow
                      project={project}
                      isExpanded={isProjectExpanded}
                      onToggle={() => toggleProject(project)}
                    />

                    {isProjectExpanded && (
                      <>
                        <tr>
                          <td colSpan={4} className="ps-5 py-2" style={{ backgroundColor: "rgba(0,0,0,0.01)" }}>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <ButtonGroup size="sm" className="shadow-sm">
                                <Button
                                  variant="light"
                                  onClick={() => setViewMode(project.id, VIEW_MODES.RESOURCES)}
                                  style={{ 
                                    fontSize: "0.75rem", 
                                    backgroundColor: viewMode === VIEW_MODES.RESOURCES ? 'var(--brand-primary)' : 'var(--bg-surface)', 
                                    borderColor: viewMode === VIEW_MODES.RESOURCES ? 'var(--brand-primary)' : 'var(--gray-300)',
                                    color: viewMode === VIEW_MODES.RESOURCES ? 'var(--white)' : 'var(--text-secondary)'
                                  }}
                                >
                                  {t('components.planningTab.buttons.workers', { count: project.resources?.length || 0 })}
                                </Button>
                                <Button
                                  variant="light"
                                  onClick={() => setViewMode(project.id, VIEW_MODES.WORK_PACKAGES)}
                                  style={{ 
                                    fontSize: "0.75rem", 
                                    backgroundColor: viewMode === VIEW_MODES.WORK_PACKAGES ? 'var(--brand-primary)' : 'var(--bg-surface)', 
                                    borderColor: viewMode === VIEW_MODES.WORK_PACKAGES ? 'var(--brand-primary)' : 'var(--gray-300)',
                                    color: viewMode === VIEW_MODES.WORK_PACKAGES ? 'var(--white)' : 'var(--text-secondary)'
                                  }}
                                >
                                  {t('components.planningTab.buttons.work_packages', { count: workPackages.length })}
                                </Button>
                              </ButtonGroup>

                              <InputGroup size="sm" style={{ maxWidth: "220px" }}>
                                <InputGroup.Text className="bg-surface">
                                  <FaSearch size={11} className="txt-subtle" />
                                </InputGroup.Text>
                                <Form.Control
                                  placeholder={viewMode === VIEW_MODES.RESOURCES ? t('components.planningTab.search.person') : t('components.planningTab.search.person_or_wp')}
                                  value={searchByProject[project.id] || ""}
                                  onChange={(e) => setSearch(project.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: "0.8rem" }}
                                />
                              </InputGroup>
                            </div>
                          </td>
                        </tr>

                        {viewMode === VIEW_MODES.RESOURCES ? (
                          filteredResources.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="ps-5 py-2 txt-subtle" style={{ fontSize: "0.85rem" }}>
                                {t('components.planningTab.empty_states.no_person')}
                              </td>
                            </tr>
                          ) : (
                            filteredResources.map((resource) => (
                              <ResourceRow key={`resource-${resource.id}`} resource={resource} projectName={project.name} projectId={project.id} workPackages={workPackages} month={selectedMonthKey} />
                            ))
                          )
                        ) : isLoadingPackages ? (
                          <tr>
                            <td colSpan={4} className="text-center py-3">
                              <Spinner animation="border" size="sm" style={{ color: 'var(--brand-primary)' }} />
                            </td>
                          </tr>
                        ) : filteredWorkPackages.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="ps-5 py-2 txt-subtle" style={{ fontSize: "0.85rem" }}>
                              {search ? t('components.planningTab.empty_states.no_results') : t('components.planningTab.empty_states.no_wps')}
                            </td>
                          </tr>
                        ) : (
                          filteredWorkPackages.map((wp) => (
                            <WorkPackageRow
                              key={`wp-${wp.id}`}
                              workPackage={wp}
                              resources={project.resources || []}
                              isExpanded={!!search || expandedPackages.has(wp.id)}
                              onToggle={() => togglePackage(wp.id)}
                              month={selectedMonthKey}
                            />
                          ))
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-5 txt-subtle">
                  {t('components.planningTab.empty_states.no_allocations')}
                </td>
              </tr>
            )}

          </tbody>
        </Table>
      </Card>
        </>
      )}
    </div>
  );
};

export default PlanningTab;
